import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '../components/nav';
import { loadTeaching } from '../data/load';
import { useAppData } from '../state/AppData';
import type { Teaching } from '../data/types';

/** Paquete docente (spec §16): guía, tres secuencias y soluciones razonadas, todo imprimible. */
export function Classroom() {
  const { data, error } = useTeaching();
  const { slug } = useParams();
  if (error) return <div className="page"><h1>Aula</h1><p>No se pudo cargar el paquete docente: {error}</p></div>;
  if (!data) return <div className="page" role="status"><p>Cargando el paquete docente…</p></div>;
  if (slug === 'soluciones') return <Solutions data={data} />;
  const seq = slug ? data.sequences.find((s) => s.slug === slug) : undefined;
  if (slug && !seq) {
    return <div className="page"><h1>Secuencia no encontrada</h1><p><Link to="/aula">Volver al aula</Link></p></div>;
  }
  return seq ? <SequenceView data={data} seq={seq} /> : <Guide data={data} />;
}

/** El paquete docente cubre todo el libro; en el aula se trabaja un ámbito cada vez. La guía es
 *  común y las secuencias y soluciones vienen ya partidas por colección (fase E). */
function useTeaching() {
  const [state, setState] = useState<{ col?: string; data?: Teaching; error?: string }>({});
  const { city } = useAppData();
  const col = city.collectionId;
  useEffect(() => {
    let live = true;
    loadTeaching(col)
      .then((data) => { if (live) setState({ col, data }); })
      .catch((e: unknown) => { if (live) setState({ col, error: e instanceof Error ? e.message : String(e) }); });
    return () => { live = false; };
  }, [col]);
  return state.col === col ? state : {};
}

function Guide({ data }: { data: Teaching }) {
  const g = data.guide;
  return (
    <div className="page page--aula">
      <h1>{g.title}</h1>
      <p className="itin__sub">{g.subtitle} · versión {g.version}</p>
      <p>{g.intro}</p>
      <p className="note">{g.audience}</p>

      <h2>Guía rápida</h2>
      <ul>{g.quickGuide.map((x) => <li key={x}>{x}</li>)}</ul>

      <h2>Secuencias de aula</h2>
      <ul className="itin-list">
        {data.sequences.map((s) => (
          <li key={s.id}>
            <h3><Link to={`/aula/${s.slug}`}>{s.number}. {s.title}</Link></h3>
            <p className="itin-meta">{s.stage} · {s.duration} · {s.subjects.join(', ')}</p>
            <p>{s.objectives[0]}</p>
          </li>
        ))}
      </ul>
      <p><Link to="/aula/soluciones">Soluciones razonadas de los {data.solutions.length} retos</Link></p>

      <h2>Cómo se marcan las fuentes</h2>
      <p>{g.sourceLiteracy}</p>
      <h2>Qué significa el estado actual</h2>
      <p>{g.statusExplained}</p>

      <h2>Antes de empezar</h2>
      <ul>{g.beforeYouStart.map((x) => <li key={x}>{x}</li>)}</ul>
      <h2>Sin mapa o con poca conexión</h2>
      <ul>{g.noMapAlternatives.map((x) => <li key={x}>{x}</li>)}</ul>
      <h2>Accesibilidad</h2>
      <ul>{g.accessibility.map((x) => <li key={x}>{x}</li>)}</ul>

      <h2>El texto de 1884 en clase</h2>
      <p>{g.ethics}</p>

      <h2>Evaluación</h2>
      <p>{g.assessment}</p>
      <table className="rubric">
        <caption className="visually-hidden">Rúbrica de evaluación por criterios y niveles</caption>
        <thead><tr><th scope="col">Criterio</th><th scope="col">1</th><th scope="col">2</th><th scope="col">3</th><th scope="col">4</th></tr></thead>
        <tbody>
          {g.rubrics.map((r) => (
            <tr key={r.criterion}><th scope="row">{r.criterion}</th>{r.levels.map((l, i) => <td key={i}>{l}</td>)}</tr>
          ))}
        </tbody>
      </table>

      {g.project && (
        <>
          <h2>{g.project.title}</h2>
          <p>{g.project.sessions}. {g.project.summary}</p>
          <ol>{g.project.steps.map((s) => <li key={s}>{s}</li>)}</ol>
          <p><strong>Producto final.</strong> {g.project.deliverable}</p>
        </>
      )}

      <h2>Glosario</h2>
      <dl className="kv kv--wide">
        {g.glossary.map((t) => <div key={t.term}><dt>{t.term}</dt><dd>{t.definition}</dd></div>)}
      </dl>

      <h2>Fuentes, derechos y datos abiertos</h2>
      <p>{g.rights}</p>
      <h2>Privacidad</h2>
      <p>{g.privacy}</p>
      <p className="itin__meta">Revisión: {g.lastReviewed} · <Link to="/proyecto/fuentes">Método y fuentes</Link></p>
    </div>
  );
}

function SequenceView({ data, seq }: { data: Teaching; seq: Teaching['sequences'][number] }) {
  return (
    <div className="page page--aula">
      <p className="itin__back"><Link to="/aula">← Guía docente</Link></p>
      <h1>{seq.title}</h1>
      <p className="itin-meta">{seq.stage} · {seq.duration} · {seq.subjects.join(', ')}</p>

      <h2>Objetivos de aprendizaje</h2>
      <ul>{seq.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
      <dl className="kv kv--wide">
        <div><dt>Competencias</dt><dd>{seq.competences.join(', ')}</dd></div>
        <div><dt>Conocimientos previos</dt><dd>{seq.priorKnowledge}</dd></div>
        <div><dt>Materiales</dt><dd>{seq.materials.join('; ')}</dd></div>
        <div><dt>Agrupamiento</dt><dd>{seq.grouping}</dd></div>
        <div><dt>Preparación docente</dt><dd>{seq.preparation}</dd></div>
      </dl>

      <h2>Recursos de la aplicación</h2>
      <ul>
        {seq.resources.map((r) => (
          <li key={r.id}><Link to={r.kind === 'itinerary' ? `/itinerarios/${r.slug}` : `/retos/${r.slug}`}>{r.title}</Link></li>
        ))}
      </ul>

      <h2>Desarrollo</h2>
      <table className="rubric">
        <caption className="visually-hidden">Desarrollo minuto a minuto</caption>
        <thead><tr><th scope="col">Minutos</th><th scope="col">Fase</th><th scope="col">Cómo</th></tr></thead>
        <tbody>{seq.development.map((d) => <tr key={d.minutes}><th scope="row">{d.minutes}</th><td>{d.what}</td><td>{d.how}</td></tr>)}</tbody>
      </table>

      <h2>Evidencias esperadas</h2>
      <ul>{seq.expectedEvidence.map((e) => <li key={e}>{e}</li>)}</ul>
      <h2>Atención a la diversidad</h2>
      <ul>{seq.diversity.map((d) => <li key={d}>{d}</li>)}</ul>

      <section className="ficha">
        <h2>Ficha del alumnado</h2>
        <p className="itin-meta">Imprimible: usa la función de imprimir del navegador.</p>
        <ol>{seq.studentSheet.map((q) => <li key={q}>{q}<span className="ficha__line" aria-hidden="true" /></li>)}</ol>
      </section>

      <p><strong>Evaluación.</strong> {seq.assessment}</p>
      {seq.extension && <p><strong>Extensión.</strong> {seq.extension}</p>}
      {seq.closing && <p><strong>Cierre.</strong> {seq.closing}</p>}
      <p><Link to="/aula/soluciones">Soluciones razonadas</Link> · <Link to="/aula">Guía docente</Link></p>
      <p className="itin__meta">Licencia CC BY 4.0 · {data.guide.lastReviewed}</p>
    </div>
  );
}

function Solutions({ data }: { data: Teaching }) {
  return (
    <div className="page page--aula">
      <p className="itin__back"><Link to="/aula">← Guía docente</Link></p>
      <h1>Soluciones razonadas</h1>
      <p className="note">
        Generadas a partir de los retos publicados: si cambia una respuesta, una fuente o una cita, esta página
        cambia con ella. Material para el profesorado.
      </p>
      {data.solutions.map((s) => (
        <section key={s.activityId}>
          <h2>{s.title}</h2>
          <ol className="soluciones">
            {s.items.map((it) => (
              <li key={it.n}>
                <p className="soluciones__q">{it.question}</p>
                <p><strong>Respuesta.</strong> {it.answer}</p>
                {it.reasoning && <p><strong>Razón.</strong> {it.reasoning}</p>}
                <p className="itin-meta">{it.reference}</p>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
